import { IconBriefcase, IconChevronDown, IconCurrentLocation } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useMemo, useState } from 'react';
import {
    Dropdown, DropdownToggle, DropdownMenu,
} from 'reactstrap';
import classNames from 'classnames';
import { useAuthContext } from 'contexts/AuthContext';
import useGetOrganizationDealsLite from 'views/Deals/hooks/useGetOrganizationDealsLite';
import { useUserSubprojects } from 'views/ProFormaTable/hooks';
import { SUBPROJECT_LIFE_STAGES } from 'bricks/constants';
import {
    ACTIVE_DEAL_LABEL,
    SELECT_PROJECT_OR_DEAL_LABEL,
    SIDE_NAV_TEST_IDS,
} from '../../constants';
import ProjectsDealsMenu from './ProjectsDealsMenu/ProjectsDealsMenu';
import './SideNavProjectsDealsDropdown.scss';

interface ISideNavProjectsDealsDropdownProps {
    isCollapsed: boolean;
    dealId?: string;
}

const SideNavProjectsDealsDropdown = ({ isCollapsed, dealId }: ISideNavProjectsDealsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleOpen = () => setIsOpen(prev => !prev);
    const closeDropdown = () => setIsOpen(false);

    const { selectedOrganizationId, hasDevelopmentAccess } = useAuthContext();
    const { data: deals } = useGetOrganizationDealsLite({
        orgId: selectedOrganizationId!,
        enabled: Boolean(selectedOrganizationId),
    });

    const { data: projects } = useUserSubprojects({
        orgId: selectedOrganizationId!,
        enabled: hasDevelopmentAccess && Boolean(selectedOrganizationId),
    });

    const activeProjects = useMemo(
        () => projects?.filter(
            project => project.subproject.lifeStage === SUBPROJECT_LIFE_STAGES.ACTIVE,
        ),
        [projects],
    );

    const isDealLevelNav = Boolean(dealId);
    const currentDealId = dealId ? Number(dealId) : undefined;
    const activeDeal = isDealLevelNav ? deals?.find(deal => deal.id === currentDealId) : undefined;
    const selectorAriaLabel = isDealLevelNav
        ? (activeDeal?.dealName || SELECT_PROJECT_OR_DEAL_LABEL)
        : SELECT_PROJECT_OR_DEAL_LABEL;

    const toggleContent = (
        <>
            <NSIcon
                icon={isDealLevelNav ? IconBriefcase : IconCurrentLocation}
                className="flex-shrink-0"
            />
            {!isCollapsed && (
                <>
                    <div className={classNames(
                        'flex-grow-1 SideNavProjectsDealsDropdown__label',
                        isDealLevelNav ? 'px-2' : 'py-1 pl-2 font-12',
                    )}
                    >
                        {isDealLevelNav ? (
                            <>
                                <div className="text-muted text-truncate font-10">{ACTIVE_DEAL_LABEL}</div>
                                <div className="text-dark text-truncate">{activeDeal?.dealName}</div>
                            </>
                        ) : (
                            <div className="text-muted text-truncate">{SELECT_PROJECT_OR_DEAL_LABEL}</div>
                        )}
                    </div>
                    <NSIcon
                        className={classNames('flex-shrink-0 SideNavProjectsDealsDropdown__caret', {
                            'SideNavProjectsDealsDropdown__caret--open': isOpen,
                        })}
                        icon={IconChevronDown}
                        data-testid="caretIcon"
                    />
                </>
            )}
        </>
    );

    return (
        <div className={classNames('SideNavProjectsDealsDropdown d-flex flex-column', {
            'border border-muted': !isCollapsed,
        })}
        >
            <Dropdown
                isOpen={isOpen}
                toggle={toggleOpen}
                direction={isCollapsed ? 'right' : 'down'}
                className="w-100"
            >
                <DropdownToggle
                    tag="div"
                    caret={false}
                    role="button"
                    tabIndex={0}
                    aria-label={selectorAriaLabel}
                    data-testid={SIDE_NAV_TEST_IDS.PROJECT_SELECTOR}
                    className={classNames(
                        'SideNavProjectsDealsDropdown__toggle d-flex align-items-center cursor--pointer',
                        isCollapsed ? 'justify-content-center' : 'px-2',
                    )}
                >
                    {toggleContent}
                </DropdownToggle>

                <DropdownMenu className="w-100 mt-1 p-0 SideNavProjectsDealsDropdown__menu">
                    <ProjectsDealsMenu
                        projects={hasDevelopmentAccess ? activeProjects : undefined}
                        deals={deals}
                        currentDealId={currentDealId}
                        onSelect={closeDropdown}
                    />
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default SideNavProjectsDealsDropdown;
